import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { applicationApi } from '../api/application';
import { ApplicationCreateRequest } from '../types/application';

const ApplicationSetupPage: React.FC = () => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ApplicationCreateRequest>();

  const createMutation = useMutation({
    mutationFn: applicationApi.createMyApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myApplication'] });
    },
  });

  const onSubmit = async (data: ApplicationCreateRequest) => {
    try {
      await createMutation.mutateAsync(data);
    } catch (error) {
      console.error('アプリケーションの作成に失敗しました:', error);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <CheckCircleIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1">
              初回セットアップ
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              アプリケーションの基本情報とサブスクライト連携設定を行います
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 4 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  基本情報
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="アプリケーション名"
                  {...register('name', {
                    required: 'アプリケーション名は必須です',
                    minLength: {
                      value: 2,
                      message: 'アプリケーション名は2文字以上で入力してください',
                    },
                    maxLength: {
                      value: 100,
                      message: 'アプリケーション名は100文字以下で入力してください',
                    },
                  })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="説明"
                  multiline
                  rows={4}
                  {...register('description', {
                    required: '説明は必須です',
                    maxLength: {
                      value: 1000,
                      message: '説明は1000文字以下で入力してください',
                    },
                  })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="バージョン"
                  {...register('version', {
                    required: 'バージョンは必須です',
                    pattern: {
                      value: /^\d+\.\d+\.\d+$/,
                      message: 'バージョンは「x.y.z」の形式で入力してください（例：1.0.0）',
                    },
                  })}
                  error={!!errors.version}
                  helperText={errors.version?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                  サブスクライト連携設定
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="サブスクライトAPIキー"
                  type="password"
                  {...register('subsclight_api_key', {
                    required: 'サブスクライトAPIキーは必須です',
                    minLength: {
                      value: 10,
                      message: 'APIキーは10文字以上で入力してください',
                    },
                  })}
                  error={!!errors.subsclight_api_key}
                  helperText={errors.subsclight_api_key?.message || 'サブスクライトで発行されたAPIキーを入力してください'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="サブスクライトサイト識別子"
                  {...register('subsclight_site', {
                    required: 'サブスクライトサイト識別子は必須です',
                    pattern: {
                      value: /^[a-zA-Z0-9\-_]+$/,
                      message: 'サイト識別子は英数字、ハイフン、アンダースコアのみ使用できます',
                    },
                  })}
                  error={!!errors.subsclight_site}
                  helperText={errors.subsclight_site?.message || 'サブスクライト上のサイト識別子を入力してください（例：myapp-site）'}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={createMutation.isPending}
                    sx={{ minWidth: 200 }}
                  >
                    {createMutation.isPending ? (
                      <CircularProgress size={24} />
                    ) : (
                      'セットアップ完了'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {createMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            セットアップに失敗しました。入力内容を確認してもう一度お試しください。
          </Alert>
        )}

        {createMutation.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            セットアップが完了しました！アプリケーション設定画面に移動します。
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default ApplicationSetupPage;